import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useTheme } from '@mui/material/styles';
import { allergiesAnswers, conditionsAnswers } from '../../../constants';

import { Button, RadioGroup, Box, Alert, IRadioProps, Modal } from '@/shared/ui';
import { ConfirmModal } from '@/shared/components/confirm-modal';
import { OnboardingController } from '@/shared/endpoints/onboarding';
import { CustomersStepTypeEnum, ICustomersUserDetails } from '@/shared/endpoints/customers';
import { AllergyDTO } from '@/shared/types/interfaces/transfer-prescriptions';
import { ConditionDTO, IConvertedAllergy, IConvertedCondition } from '@/shared/endpoints/onboarding/models/dtos';

import WavingImage from '@/assets/images/waving.png';
import HIPPA from '@/assets/images/hippa.png';
import HIPPAICON from '@/assets/images/hippa-icon.png';

import { constructUnknownCondition, getConditionIdentifier } from '../helpers';
import { SplitLayout } from '@/modules/onboarding/components/split-layout';

import HelpArrowRotatedIcon from '@/assets/icons/help-arrow-rotated.svg';
import { AutoCompleteSearch } from '@/shared/ui/autocomplete/autoComplete-search';
import UnsavedDataModalContents from './unsaved-data-modal-contents';

interface Allergy {
    allergyType: string;
    allergenDescription: string;
    allergenFdbId: number;
}

interface Condition {
    code: string;
    qualifierType: string;
    shortDescription: string;
}

interface AboutYouAllergiesDetails {
    allergies?: number;
    conditions?: number;
}

interface AboutYouAllergiesDefaultValues {
    details?: ICustomersUserDetails;
    allergies?: Allergy[];
    conditions?: Condition[];
}

const noKnownAllergy: Allergy = {
    allergyType: "Specific_Allergen_Group",
    allergenDescription: "No Known Allergies",
    allergenFdbId: 900388
}

// true when the stored allergy list has nothing other than the "No Known Allergies" placeholder
const isOnlyNoKnownAllergy = (allergies?: Array<{ allergenFdbId: number }>): boolean =>
    !!allergies?.length && allergies.every(_item => _item.allergenFdbId === noKnownAllergy.allergenFdbId);

interface AboutYouAllergiesAndConditionsFormProps {
    onFormSubmit: (values: AllergiesFormValues) => void;
    dynamicUpdate?: (values: AllergiesFormValues) => void;
    unsavedChanges?: boolean;
    setUnsavedChanges?: (value: boolean) => void;
    onGoBack: () => void;
    defaultValues?: AboutYouAllergiesDefaultValues;
    details?: AboutYouAllergiesDetails;
    loading?: boolean;
    showHelper?: boolean;
}
interface AboutYouAllergiesropsAndConditions {
    formArttributes: AboutYouAllergiesAndConditionsFormProps;
    introductionPassed: boolean;
}
export const HIPAAComponent = () => (
    <div data-testid="about-you-hippa-container" className="flex w-full flex-row items-center justify-center">
        <img src={HIPPAICON} alt="HIPPA" />
        <img src={HIPPA} alt="HIPPA" />
    </div>
);
export const MainTitle = () => (
    <h1 data-testid="about-you-allergies-and-condition-main-title" className="about-you-title allergies-and-conditions-main-title xl-min:about-you-title-lg xl-min:allergies-and-conditions-main-title-lg w-[auto] text-center xl-min:w-full xl-min:text-left">
        Please inform us of any <span className="text-mint-green">Medical Conditions or Allergies</span> you may have.
    </h1>
);

export const Illustration = () => (
    <div data-testid="about-you-illustration-container" className="static flex w-full flex-col items-center justify-center md:relative desktop-min:block">
        <div className="illusTextContainer w-full max-w-[27rem]">
            <div className="illus-text">
                <span data-testid="about-you-illustration-privacy-text" className="sm-max:illusTextSmall">We take your privacy seriously and strictly abide by</span>
                <span data-testid="about-you-illustration-hippa-privacy-text" className="sm-max:illusTextSmall font-atkinsonBold italic"> HIPAA Privacy Rule </span>
                <span data-testid="about-you-illustration-confidentiality-text" className="sm-max:illusTextSmall">to ensure the confidentiality of your information.</span>
            </div>
            <img
                src={WavingImage}
                alt="About you image"
                className="waiving lg-tablet:right-[1rem] -bottom-[10rem] hidden h-auto w-[8.8125rem] md:right-[2rem] xl-min:block desktop-min:absolute"
            />
        </div>
    </div>
);

export interface AllergiesFormValues {
    allergies: IConvertedAllergy[];
    conditions: IConvertedCondition[];
    hasAllergies: number | null;
    hasConditions: number | null;
    currentStep?: CustomersStepTypeEnum;
}

const SearchLabel = ({ children, error }: { children: string; error?: boolean }) => (
    <p className={`mb-[.4375rem] mt-[0.9375rem] sm:mt-[1.875rem] text-left font-atkinsonBold text-18 sm:text-20 ${error ? 'text-red-500' : ''}`}>
        {children}
    </p>
);

const SectionTitle = ({ children }: { children: string }) => (
    <h3 className="w-full text-left font-atkinsonBold font-bold text-17 sm:text-21 mb-[10px] leading-[130%] tracking-[0.34px] sm:tracking-[0.42px]">
        {children}
    </h3>
);

export const AllergiesAndConditionsForm: React.FC<AboutYouAllergiesAndConditionsFormProps> = props => {
    const theme = useTheme();
    const [unsavedChanngesModalOpen, setUnsavedChangesModalOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const normalizeAllergies = (value?: string | number): number | null => {
        if (value === undefined) return null;
        const num = Number(value);
        return num === 3 ? null : num;
    }
    const initialValues = useMemo(
        (): AllergiesFormValues => {
            return {
                conditions: props.defaultValues?.conditions
                    ? props.defaultValues.conditions.map((_item: Condition) => ({
                        ..._item,
                        label: _item.shortDescription,
                        value: getConditionIdentifier(_item)
                    }))
                    : [],
                allergies: props.defaultValues?.allergies
                    ? props.defaultValues.allergies.map((_item: Allergy) => ({
                        ..._item,
                        label: _item.allergenDescription,
                        value: _item.allergenFdbId
                    }))
                    : [],
                hasAllergies: normalizeAllergies(props.defaultValues?.details?.allergies) ?? (isOnlyNoKnownAllergy(props.defaultValues?.allergies) ? 2 : null),
                hasConditions: props.defaultValues?.details?.conditions === undefined ? null : Number(props.defaultValues.details.conditions)
            }
        },
        [props.defaultValues]
    );

    const formik = useFormik({
        validateOnChange: true,
        initialValues,
        onSubmit: values => {
            props.onFormSubmit(values);
        }
    });

    const [allergiesOpts, setAllergieOpts] = React.useState<IConvertedAllergy[]>([]);
    const [conditionOpts, setConditionOpts] = React.useState<IConvertedCondition[]>([]);
    const [isFormLoading, setIsFormLoading] = React.useState<boolean>(false);
    const [isAllergiesModalOpen, setIsAllergiesModalOpen] = React.useState<boolean>(false);
    const [isConditionsModalOpen, setIsConditionsModalOpen] = React.useState<boolean>(false);
    const [nextHasCondition, setNextHasCondition] = React.useState<IRadioProps | null>(null);
    const [nextHasAllergy, setNextHasAllergy] = React.useState<IRadioProps | null>(null);

    const handleConditionsChange = useCallback(
        (condition: IConvertedCondition) => {
            const item = conditionOpts.find(_item => {
                if (_item.code.toLowerCase() === 'unknown') {
                    // this case is for the unknown medication we build
                    return _item.value === condition.value
                } else {
                    return _item.code === condition.value
                }
            });

            const existing = formik.values.conditions || [];
            if (item && existing.find(_item => {
                const existingIdentifier = getConditionIdentifier(_item);
                const newIdentifier = getConditionIdentifier(item);
                return existingIdentifier === newIdentifier;
            })) {
                return;
            }

            formik.setFieldValue('conditions', [condition, ...existing]);
        },
        [formik, conditionOpts]
    );

    const handleAllergiesChange = (allergy: IConvertedAllergy) => {
        const { value } = allergy;
        const item = allergiesOpts.find(_item => _item.allergenFdbId === value);
        const existing = formik.values.allergies || [];
        if (item && existing.find(_item => _item.allergenFdbId === item.allergenFdbId)) {
            return;
        }

        formik.setFieldValue('allergies', [allergy, ...existing]);
    };

    // like allergies, when the user tries to update the conditions, we need to update the options
    const handleConditionsInputChange = useCallback(async (value: string, signal: AbortSignal): Promise<ConditionDTO[]> => {
        setIsFormLoading(true);

        try {
            const res =
                (await OnboardingController.searchConditions({
                    term: value,
                    limit: 5
                }, signal)) || [];

            const resWithUnknownCondition = [constructUnknownCondition(value), ...res];

            const allConditions = resWithUnknownCondition.map(_item => ({
                label: _item.shortDescription,
                value: getConditionIdentifier(_item),
                ..._item
            }));
            setConditionOpts(allConditions as IConvertedCondition[]);
            return allConditions;
        } catch (error) {
            setConditionOpts([]);
            return [];
        } finally {
            setIsFormLoading(false);
        }
    }, []);

    // when input of the auto-complete changes, we need to update the options for allergies
    const handleAllergiesInputChange = useCallback(async (value: string, signal: AbortSignal): Promise<AllergyDTO[]> => {
        setIsFormLoading(true);
        try {
            const res: Array<AllergyDTO> = await OnboardingController.searchAllergies({
                term: value,
                limit: 5
            }, signal);

            const allAllergies = res?.length
                ? res.map((_item: AllergyDTO) => ({
                    label: _item.allergenDescription,
                    value: _item.allergenFdbId,
                    ..._item
                }))
                : [];

            setAllergieOpts(allAllergies);
            return allAllergies;
        } catch (error) {
            setAllergieOpts([]);
            return [];
        } finally {
            setIsFormLoading(false);
        }
    }, []);

    // when useer switch between "yes" and "no", do correspodning check, and pop up the modal if necessary
    const handleHasAllergiesClick = useCallback(
        (item: IRadioProps) => {
            // from yes to no, need the confirm
            if (formik.values.hasAllergies === item.value) {
                return;
            }
            // if the user selects option 1 we need to remove the no known allergy from the list
            if (item.value === 1) {
                formik.setFieldValue('allergies', []);
                formik.setFieldTouched('allergies', false);
            } else {
                // only prompt for removal confirmation if there are real allergies, not just the placeholder
                const hasRealAllergies = formik.values.allergies?.some(_item => _item.allergenFdbId !== noKnownAllergy.allergenFdbId);
                if (hasRealAllergies) {
                    //  show modal and wait for confirm
                    setNextHasAllergy(item);
                    return setIsAllergiesModalOpen(true);
                } else {
                    formik.setFieldValue('allergies', [noKnownAllergy]);
                    formik.setFieldTouched('allergies', false);
                }
            }
            formik.setFieldValue('hasAllergies', item.value);
        },
        [formik]
    );

    // the options of conditions are 1,2,3
    const handleHasConditionsClick = useCallback(
        (item: IRadioProps) => {
            if (formik.values.hasConditions === item.value) {
                return;
            }
            if (formik.values.hasConditions === 1 && item.value !== 1 && formik.values.conditions && formik.values.conditions.length) {
                // show modal and wait for confirm
                setNextHasCondition(item);
                return setIsConditionsModalOpen(true);
            }
            formik.setFieldValue('hasConditions', item.value);
            // setHasConditions(item.value);
        },
        [formik]
    );

    const handleAllergiesCofirm = useCallback(() => {
        setIsAllergiesModalOpen(false);
        formik.setFieldValue('allergies', [noKnownAllergy]);
        formik.setFieldValue('hasAllergies', nextHasAllergy?.value);
        formik.setFieldTouched('allergies', false);
    }, [formik, nextHasAllergy]);

    const handleConditionsConfirm = useCallback(() => {
        setIsConditionsModalOpen(false);
        setNextHasCondition(null);
        formik.setFieldValue('hasConditions', nextHasCondition?.value);
        formik.setFieldValue('conditions', []);
        formik.setFieldTouched('conditions', false);
    }, [formik, nextHasCondition?.value]);

    const handleAllergiesRemove = useCallback(
        (item: IConvertedAllergy) => {
            const remian = formik.values.allergies?.filter(_item => _item.value !== item.value);
            formik.setFieldValue('allergies', remian);
            formik.setFieldTouched('allergies', true);
        },
        [formik]
    );

    const handleConditionsRemove = useCallback(
        (item: IConvertedCondition) => {
            const remian = formik.values.conditions?.filter(_item => _item.value !== item.value);
            formik.setFieldValue('conditions', remian);
            formik.setFieldTouched('conditions', true);
        },
        [formik]
    );

    const validateForm = useCallback((updateErrorState = true) => {
        const { hasAllergies, hasConditions, allergies, conditions } = formik.values;
        if (updateErrorState) {
            formik.setFieldError('hasAllergies', undefined);
            formik.setFieldError('hasConditions', undefined);
            formik.setFieldError('allergies', undefined);
            formik.setFieldError('conditions', undefined);
        }
        let hasError = false;
        if (hasAllergies === null || hasAllergies === undefined) {
            updateErrorState && formik.setFieldError('hasAllergies', 'Please make a selection to continue');
            hasError = true;
        } else if (hasAllergies === 1 && (!allergies || !allergies.length)) {
            updateErrorState && formik.setFieldError('allergies', 'Please add your medication allergies to continue');
            hasError = true;
        }
        if (hasConditions === null || hasConditions === undefined) {
            updateErrorState && formik.setFieldError('hasConditions', 'Please make a selection to continue');
            hasError = true;
        } else if (hasConditions === 1 && (!conditions || !conditions.length)) {
            updateErrorState && formik.setFieldError('conditions', 'Please add your medical conditions to continue');
            hasError = true;
        }

        return hasError;
    }, [formik]);

    const areArraysEqualByKey = <T, U>(
        arr1: T[] = [],
        arr2: U[] = [],
        getKey1: (item: T) => string | number,
        getKey2: (item: U) => string | number
    ): boolean => {
        if (arr1.length !== arr2.length) return false;

        const set1 = new Set(arr1.map(getKey1));
        const set2 = new Set(arr2.map(getKey2));

        if (set1.size !== set2.size) return false;

        for (const val of set1) {
            if (!set2.has(val)) return false;
        }

        return true;
    };

    useEffect(() => {
        const hasErrors = validateForm(false);
        if (!hasErrors) {
            const changedHasConditions = formik.values.hasConditions === (props.defaultValues?.details?.conditions === undefined ? null : Number(props.defaultValues.details.conditions));
            const hasChangedAllergies = formik.values.hasAllergies === normalizeAllergies(props.defaultValues?.details?.allergies);
            const hasSameAllergies = areArraysEqualByKey(
                formik.values.allergies,
                props.defaultValues?.allergies,
                a => a.allergenFdbId,
                b => b.allergenFdbId
            );

            const hasSameConditions = areArraysEqualByKey(
                formik.values.conditions,
                props.defaultValues?.conditions,
                a => a.code,
                b => b.code
            );
            props.setUnsavedChanges && props.setUnsavedChanges(false);
            if (changedHasConditions && hasChangedAllergies && hasSameAllergies && hasSameConditions) {
                return;
            }
            props.dynamicUpdate && props.dynamicUpdate(formik.values);
        } else {
            props.setUnsavedChanges && props.setUnsavedChanges(true);
        }
    }, [formik.values]);

    const closeUnsavedChangesModal = useCallback(() => {
        setUnsavedChangesModalOpen(false);
    }, []);

    const goBackIfApplicable = useCallback(() => {
        if (props.onGoBack) {
            props.onGoBack();
        }
    }, [props]);

    const formikValues = formik.values;

    useEffect(() => {
        if (submitted) setTimeout(() => validateForm(), 0);
    }, [submitted, formikValues]);


    const dynamicAllergiesSelectionBasedOnIncomingAllergySelectionFromDetails = useMemo(() => {
        if (props?.defaultValues?.details?.allergies !== 3) {
            return allergiesAnswers.filter(item => item.value !== 3) as IRadioProps[];
        }
        return allergiesAnswers as IRadioProps[];
    }, [props?.defaultValues?.details?.allergies]);


    return (
        <div data-testid="allergies-wrapper">
            <Box
                component="form"
                data-testid="allergies-form"
                onSubmit={(e: FormEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    setSubmitted(true);
                    const hasError = validateForm();
                    // @ts-ignore-next-line
                    if (!hasError) formik.handleSubmit(e);
                }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '30px',
                    [theme.breakpoints.down('md')]: {
                        gap: '25px'
                    },
                    width: '100%',
                    backgroundColor: '#fff'
                }}
                padding='0px'
                paddingLg='0px'
                isLoading={isFormLoading}
            >
                <div className="flex w-full flex-col gap-[1.5625rem] sm:gap-[1.875rem] rounded-lg bg-white md-min:gap-[1.88rem]">
                    <section className="allergies-section aboutyou-form-sections">
                        <SectionTitle>Do you have any allergies to medications?</SectionTitle>
                        <div className="aboutyou-form-section-content desktop-min:aboutYouFormSectionContentLg flex w-full flex-col gap-[1.56rem] rounded-[0.8125rem]">
                            <div className="about-you-checkbox flex-no-wrap flex flex-col">
                                <RadioGroup
                                    controlled
                                    items={dynamicAllergiesSelectionBasedOnIncomingAllergySelectionFromDetails}
                                    name="allergies"
                                    dataTestId={'allergies'}
                                    radioGroupClassName="allergies-and-conditions-radio-group flex-row gap-[1.25rem]"
                                    buttonClassName="allergies-and-conditions-radio-button"
                                    value={formik.values.hasAllergies}
                                    onOptionClick={handleHasAllergiesClick}
                                />
                                {formik.values.hasAllergies === 1 && (
                                    <div className="w-full">
                                        <SearchLabel error={!!formik.errors.hasAllergies || !!(formik.values.hasAllergies === 1 && formik.errors.allergies)}>Search the medication allergy</SearchLabel>
                                        <AutoCompleteSearch
                                            scrollToTitle
                                            chipClassName='!bg-white !text-primary !border-solid !border-primary !border-2 px-[20px] py-[8px] font-dmSans text-18 !font-dmSansMedium !font-bold !leading-[105%]'
                                            selectedOptionsClassName='!bg-mint-400 !p-[7px] !rounded-[8px]'
                                            uniqueIdentifier="allergiesFinder"
                                            placeholder="Type one allergy at a time"
                                            onChange={handleAllergiesChange}
                                            onSearch={handleAllergiesInputChange}
                                            popoverPlaceholder="Type one allergy at a time"
                                            labelKey="label"
                                            error={
                                                !!formik.errors.hasAllergies ||
                                                !!(formik.values.hasAllergies === 1 && formik.errors.allergies)
                                            }
                                            popoverHelper={
                                                <i className="absolute right-[26px] z-[10] flex items-start font-dmSans text-[1.0625rem] tracking-[0.02rem]">
                                                    <p className="mr-2 mt-2">
                                                        It's <b>3x faster</b> <br /> using voice!
                                                    </p>
                                                    <img src={HelpArrowRotatedIcon} alt="help arrow" className="w-[53px]" />
                                                </i>
                                            }
                                            onSelectedRemove={handleAllergiesRemove}
                                            selected={formik.values.allergies || []}
                                            inputVariant="advanced"
                                            popoverInputVariant="advanced"
                                            popoverTitle="Search the medication allergy"
                                        />
                                    </div>
                                )}
                                {(formik.errors.hasAllergies || (formik.values.hasAllergies === 1 && formik.errors.allergies)) && (
                                    <div className={`${formik.values.hasAllergies === 1 && formik.errors.allergies ? 'mt-[10px]' : 'mt-[15px] sm:mt-[20px]'}`}>
                                        <Alert boldLabel={false} className='font-atkinsonHyperlegible text-15 !bg-peach-600' label={formik.errors.hasAllergies || formik.errors.allergies as string} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                    <section className="conditions-section aboutyou-form-sections">
                        <SectionTitle >Do you have any medical conditions?</SectionTitle>
                        <div className="aboutyou-form-section-content desktop-min:aboutYouFormSectionContentLg flex w-full flex-col rounded-[0.8125rem]">
                            <RadioGroup
                                items={conditionsAnswers}
                                name="conditions"
                                dataTestId={'conditions'}
                                radioGroupClassName="allergies-and-conditions-radio-group conditions-radio-group flex-row gap-[1.25rem]"
                                buttonClassName="allergies-and-conditions-radio-button"
                                value={formik.values.hasConditions}
                                onOptionClick={handleHasConditionsClick}
                                controlled
                            />
                            {formik.values.hasConditions === 1 && (
                                <div className="w-full">
                                    <SearchLabel error={!!formik.errors.hasConditions || !!(formik.values.hasConditions === 1 && formik.errors.conditions)}>Search the medical condition</SearchLabel>
                                    <AutoCompleteSearch
                                        scrollToTitle
                                        chipClassName='!bg-white !text-primary !border-solid !border-primary !border-2 px-[20px] py-[8px] font-dmSans text-18 !font-dmSansMedium !font-bold !leading-[105%]'
                                        selectedOptionsClassName='!bg-mint-400 !p-[7px] !rounded-[8px]'
                                        uniqueIdentifier="conditionsFinder"
                                        placeholder="Type one condition at a time"
                                        popoverPlaceholder="Type one condition at a time"
                                        labelKey="label"
                                        onChange={handleConditionsChange}
                                        onSearch={handleConditionsInputChange}
                                        onSelectedRemove={handleConditionsRemove}
                                        inputVariant="advanced"
                                        popoverInputVariant="advanced"
                                        popoverTitle="Search the medical condition"
                                        error={
                                            !!formik.errors.hasConditions ||
                                            !!(formik.values.hasConditions === 1 && formik.errors.conditions)
                                        }
                                        popoverHelper={
                                            <i className="absolute right-[26px] z-[10] flex items-start font-dmSans text-[1.0625rem] tracking-[0.02rem]">
                                                <p className="mr-2 mt-2">
                                                    It's <b>3x faster</b> <br /> using voice!
                                                </p>
                                                <img src={HelpArrowRotatedIcon} alt="help arrow" className="w-[53px]" />
                                            </i>
                                        }
                                        selected={formik.values.conditions || []}
                                        startAdornmentSx={{
                                            '@media only screen and (min-width: 768px) and (max-width: 950px)': {
                                                display: 'none'
                                            }
                                        }}
                                    />
                                </div>
                            )}
                            {(formik.errors.hasConditions || (formik.values.hasConditions === 1 && formik.errors.conditions)) && (
                                <div className={`${formik.values.hasConditions === 1 && formik.errors.conditions ? 'mt-[10px]' : 'mt-[15px] sm:mt-[20px]'}`}>
                                    <Alert boldLabel={false} className='font-atkinsonHyperlegible text-15 sm:text-16 leading-[137%] !bg-peach-600' label={formik.errors.hasConditions || formik.errors.conditions as string} />
                                </div>
                            )}
                        </div>
                    </section>
                </div>
                <div className="flex w-full flex-col items-center justify-start">
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        showEndIcon
                        className="mb-[1.25rem] w-full md-max:mb-[0.44rem]"
                        loading={props.loading}
                        testId="button-allergies-conditions-continue"
                        fontClassName='font-bold'
                    >
                        Save & Continue
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            if (props.unsavedChanges) {
                                setUnsavedChangesModalOpen(true);
                                return;
                            } else {
                                goBackIfApplicable();
                            }
                        }}
                        className="w-full"
                        disabled={props.loading}
                        testId="button-allergies-conditions-back"
                        fontClassName='font-bold'
                    >
                        Back
                    </Button>
                </div>
                <section className="conditions-section aboutyou-form-sections">
                    <HIPAAComponent />
                </section>
            </Box>

            <ConfirmModal
                open={isAllergiesModalOpen}
                onConfirm={handleAllergiesCofirm}
                onCancel={() => setIsAllergiesModalOpen(false)}
                title="Are you sure you want to remove your selected medical allergies?"
                confirmText="Yes, Remove"
                cancelText="Cancel Removal"
            />

            <ConfirmModal
                open={isConditionsModalOpen}
                onConfirm={handleConditionsConfirm}
                onCancel={() => setIsConditionsModalOpen(false)}
                title="Are you sure you want to remove your selected medical conditions?"
                confirmText="Yes, Remove"
                cancelText="Cancel Removal"
            />

            <Modal
                contentClassName="px-[20px] pb-[25px] pt-[40px] sm:p-[40px] !max-w-[488px]"
                onClose={closeUnsavedChangesModal}
                open={unsavedChanngesModalOpen}
                hideCloseIcon={true}
            >
                <UnsavedDataModalContents
                    leaveWithoutSaving={goBackIfApplicable}
                    saveAndContinue={closeUnsavedChangesModal}
                />
            </Modal>
        </div>
    );
};

export const AllergiesAndConditionsSideComponent = () => {
    return (
        <div data-testid="about-you-allergies-and-conditions-side-container" className="flex flex-col items-center justify-center xl-min:items-start">
            <div className="top-img-container flex w-full flex-row items-center justify-center xl-min:hidden block">
                <img src={WavingImage} alt="About you image" className="waiving block h-auto w-[7rem]" />
            </div>
            <MainTitle />
            <div className="xl-min:mr-[8rem] tablet:mr-[4rem] desktop-min:mt-[3.92rem] hidden xl:block desktop:mb-[3.92rem]">
                <Illustration />
            </div>
        </div>
    );
};

export const AllergiesAndConditionsComponent: React.FC<AboutYouAllergiesropsAndConditions> = props => {
    return (
        <SplitLayout
            sideContent={<AllergiesAndConditionsSideComponent />}
            mainContent={
                <div className="box-border flex w-full flex-1 flex-col items-center justify-start desktop-min:items-start">
                    <div className="box-wrapper desktop-min:box-wrapper-lg box-border w-full bg-white">
                        <AllergiesAndConditionsForm {...props.formArttributes} showHelper={!props.introductionPassed} />
                    </div>
                </div>
            }
        />
    );
};

export default AllergiesAndConditionsComponent;
